import { defineMessages } from '@kyruus/intl';

export const messages = defineMessages({
  providerListHeader: {
    id: 'providerlist.header',
    description: 'Header for a list of search results ',
    defaultMessage: 'Search results'
  },
  providerListExpandedSearchRadiusFullWarning: {
    id: 'providerlist.expanded_search_radius_full_warning',
    description:
      'Warning message that the distance filter was removed when no results are found for a provided search radius and even within a 100 mile radius',
    defaultMessage: `No providers found near the selected location, showing all results for "{searchDescription}"`
  },
  providerListExpandedSearchRadiusPartialWarning: {
    id: 'providerlist.expanded_search_radius_partial_warning',
    description:
      'Warning message when no results are found and the search radius is automatically expanded to get results',
    defaultMessage: `No providers found within {
        originalDistance,
        plural,
        one {# mile}
        other {# miles}
      }, showing providers within {expandedDistance, select, Any {any miles} any {any miles} other {{expandedDistance, plural, one {# mile} other {# miles}}}}
      `
  },
  alertCloseButtonAriaLabel: {
    id: 'banner.close.arialLabel1',
    description:
      'action1 close icon aria label text to be read by screen reader',
    defaultMessage: 'close'
  },
  noResultsBanner: {
    id: 'banner.noProvidersFound',
    description: 'Banner displayed when no providers are found',
    defaultMessage:
      'We are unable to locate any providers based on your search area. Try searching in a different area.'
  },

  clinical_keywords: {
    id: 'search.searchtypes.clinical_keywords',
    description: 'Category name for a suggested `clinical_keywords` search',
    defaultMessage: 'Conditions'
  },
  location_name: {
    id: 'search.searchtypes.location_name',
    description: 'Category name for a suggested `location_name` search',
    defaultMessage: 'Locations'
  },
  mapSearchLinkText: {
    id: 'map_search.map_search_link.text',
    description: 'Text of the link that opens the map search page',
    defaultMessage: 'Map'
  },
  mapSearchLinkAriaLabel: {
    id: 'map_search.map_search_link.aria_label',
    description: 'Aria label of the link that opens the map search page',
    defaultMessage: 'Map Search'
  },
  network_affiliation: {
    id: 'search.searchtypes.network_affiliation',
    description: 'Category name for a suggested `network_affiliation` search',
    defaultMessage: 'Affiliations'
  },
  provider_name: {
    id: 'search.searchtypes.provider_name',
    description: 'Category name for a suggested `provider_name` search',
    defaultMessage: 'Providers'
  },
  specialties: {
    id: 'search.searchtypes.specialties',
    description: 'Category name for a suggested `specialties` search',
    defaultMessage: 'Specialties'
  },
  closeLocationDetailsAriaLabel: {
    id: 'map_search.location_details.aria.label.close',
    description: 'Aria label for close location details',
    defaultMessage: 'Close location details'
  },
  topfilter: {
    id: 'facet.labels.topfilter',
    description: 'Label for the most popular filter',
    defaultMessage: 'Popular choices'
  },
  allfilter: {
    id: 'facet.labels.allfilter',
    description: 'Label for all other filter options',
    defaultMessage: 'All choices'
  },
  typeaheadResultsStatus: {
    id: 'facet.typeahead.results.status',
    description: 'Accessibility status for typeahead results',
    defaultMessage: `{count, plural,
      one {# result available}
      other {# results available}
    }`
  },
  typeaheadNoResultsStatus: {
    id: 'facet.typeahead.no_results.status',
    description: 'Accessibility status for typeahead no results found.',
    defaultMessage: 'No results available'
  },
  typeaheadDefaultPlaceholder: {
    id: 'facet.typeahead.placeholder.default',
    description: 'Default placeholder text for a typeahead facet',
    defaultMessage: 'Type here...'
  },
  typeaheadDescriptionLabel: {
    id: 'aria.descriptionLabel.facet.typeahead',
    description: 'Accessibility description for a typeahead facet',
    defaultMessage: 'Type here for filter options'
  },
  typeaheadRemoveFilterAriaLabel: {
    id: 'aria.label.facet.typeahead.remove_filter',
    description:
      'Accessibility label for the button that removes a selected typeahead filter',
    defaultMessage: 'Remove {filterName}'
  },
  withinLabel: {
    id: 'facet.location.within',
    description: "Title text for a relative distance, e.g. 'Within 5 miles of'",
    defaultMessage: 'Within'
  },
  within_aria_label: {
    id: 'facet.location.within.aria_label',
    description: 'Aria label for the within this distance select input',
    defaultMessage: 'Select within distance'
  },
  anymiles: {
    id: 'facet.location.anymiles',
    description: "Text to display when distance in miles is set to 'Any'",
    defaultMessage: 'Any miles'
  },
  numericalmiles: {
    id: 'facet.location.numericalmiles',
    description:
      'Text to display when distance in miles is set to a numerical value',
    defaultMessage: `{distance, plural,
      one {# mile}
      other {# miles}
    }`
  },
  placeholder: {
    id: 'facet.location.placeholder',
    description: 'Placeholder text for the location facet',
    defaultMessage: 'City, State or ZIP'
  },
  closeFilterPopoverAriaLabel: {
    id: 'filter_bar.close_filter_popover.aria_label',
    description: 'Aria label for close filter popover',
    defaultMessage: 'Close filter menu'
  },
  showmore: {
    id: 'facet.showmore',
    description: 'Title for button to show more results',
    defaultMessage: 'Show More'
  },
  showless: {
    id: 'facet.showless',
    description: 'Title for button to show fewer results',
    defaultMessage: 'Show Less'
  },
  useCurrentLocationLabel: {
    id: 'facet.location.user_current_location_label',
    description: 'Label to use on "use current location" cta',
    defaultMessage: 'Use current location'
  },
  locationInputLabel: {
    id: 'facet.location.input.label',
    description: 'Label for the location input',
    defaultMessage: 'City, State, or Zip'
  },
  locationInputLoadingLabel: {
    id: 'facet.location.input.loading.label',
    description: 'Label for the location input when loading suggestions',
    defaultMessage: 'Loading suggestions'
  },
  locationInputNoOptionsLabel: {
    id: 'facet.location.input.no_options.label',
    description: 'Label for the location input when no suggestions are found',
    defaultMessage: 'No suggestions found'
  },
  locationInputAriaLabelText: {
    id: 'facet.location.aria_label_text',
    description: 'Aria label text for the location input',
    defaultMessage:
      'Type city, state, or zip to find providers within {distance} mile{distance, plural, one {} other {s}}'
  },
  locationSearchButton: {
    id: 'facet.location.aria.label.search_button',
    description: 'Aria label text for Search Icon button',
    defaultMessage: 'Search'
  }
});
